import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SltDataServiceService } from '../../services/slt-data-service.service';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-slt-data-service',
    templateUrl: './slt-data-service.component.html',
    styleUrls: ['./slt-data-service.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SltDataServiceComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  
  globalService: any;
  appService: any = {
    USER_SETTINGS: '',
    SERVER_CONFIG: ''
  };
  GENERAL_CONFIG: any;

  rowData: any = {};

  // STANDARD COMPONENT PROPERTIES
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  
  // Component specific properties
  modalTitle = 'Data Service';
  showFormLoader = false;
  crudLoading = false;
  
  // Data service specific properties
  inParams: any[] = [];
  rowInParams: any = {};
  dataServiceResponse: string = '';
  dataServiceResponseToDownload: string = '';
  copyMessage: string = '';
  isResponseTooLarge: boolean = false;
  
  // Client and service data
  client_key: number = 0;
  tax_year: string = '';
  scenario: string = '';

  constructor(
    private sltDataServiceService: SltDataServiceService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit() {
    this.applyWorkspaceContext();
    // this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
    //   this.applyWorkspaceContext();
    //   this.init();
    // }));
    // this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
    //   this.applyWorkspaceContext();
    //   this.init();
    // }));
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.showFormLoader = false;
      this.init();
    } else {
      this.showFormLoader = true;
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.appService = { USER_SETTINGS: header.userSettingsData || {}, SERVER_CONFIG: header.serverConfigData || {} };
    this.cdr.markForCheck();
  }

  init() {
    try {
      if (this.rowData && Object.keys(this.rowData).length > 0 && 
          this.globalService && this.GENERAL_CONFIG) {
        this.setupDataServiceParams();
      }
    } catch (e) {
      console.error('Error in SltDataService init:', e);
      this.showFormLoader = false;
    }
  }

  /**
   * Setup data service parameters from rowData and globalService
   */
  private setupDataServiceParams() {
    // Set client key, tax year, scenario from rowData and globalService
    this.client_key = this.rowData.CLIENT_KEY === -1 ? 0 : this.rowData.CLIENT_KEY;
    this.tax_year = this.globalService?.globalParams?.tax_year || '';
    this.scenario = this.globalService?.globalParams?.scenario || '';
    
    // Parse PROC_PARAMS_IN from rowData
    let procInParams = this.rowData.PROC_PARAMS_IN ? this.rowData.PROC_PARAMS_IN.split(",") : [];
    
    procInParams.forEach((element: string) => {
      if (element && element.split(":").length > 2) {
        const paramParts = element.split(":");
        const paramName = paramParts[0];
        const paramValue = paramParts[2];
        
        if (paramValue !== 'null') {
          this.rowInParams[paramName] = paramValue;
        } else {
          this.rowInParams[paramName] = '';
        }
      }
    });
    
    // Add standard parameters
    this.rowInParams['client_key'] = this.client_key;
    this.rowInParams['tax_year'] = this.tax_year;
    this.rowInParams['scenario'] = this.scenario;
    this.rowInParams['jcd_key'] = this.globalService?.globalParams?.jcd_key || '';
    this.rowInParams['service_key'] = this.rowData.SERVICE_KEY || '';
    this.rowInParams['sso_id'] = this.rowData.SSO_ID || '';
    
    // Build inParams array for display
    for (let i in this.rowInParams) {
      let obj: any = {};
      obj['key'] = _.startCase(_.toLower(i));
      obj['value'] = this.rowInParams[i]; 
      obj['urlparam'] = i;
      this.inParams.push(obj);
    }
    
    this.showFormLoader = false;
    this.cdr.detectChanges();
  }

  /**
   * Test the data service by calling the API
   */
  test() {
    this.crudLoading = true;
    this.dataServiceResponse = '';
    this.dataServiceResponseToDownload = '';
    this.isResponseTooLarge = false;
    this.cdr.detectChanges();
    
    this.sltDataServiceService.testDataService(
      this.GENERAL_CONFIG.base_url,
      this.inParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        // Always stop loading first
        this.crudLoading = false;
        
        if (response && response.callSuccess === "0") {
          // Error response
          this._snackBar.open(response.errorMessage || 'Error occurred while calling API', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          this.dataServiceResponse = '';
          this.dataServiceResponseToDownload = '';
          this.isResponseTooLarge = false;
        } else if (response === null || response === undefined || (typeof response === 'object' && Object.keys(response).length === 0)) {
          // Empty/null response - still show as successful but with empty data message
          this.dataServiceResponse = '';
          this.dataServiceResponseToDownload = '';
          this.isResponseTooLarge = false;
          this._snackBar.open('API call successful but returned no data', 'Close', {
            duration: 4000,
            panelClass: ['warning-snackbar']
          });
        } else {
          // Valid response with data
          try {
            // Format for display (handles large responses)
            const formatResult = this.formatJsonResponse(response);
            this.dataServiceResponse = formatResult.formattedResponse;
            this.isResponseTooLarge = formatResult.isTooLarge;
            
            // Store stringified response for download (try-catch to handle large objects)
            try {
              this.dataServiceResponseToDownload = JSON.stringify(response);
            } catch (stringifyError) {
              console.error('Error stringifying response for download:', stringifyError);
              this.dataServiceResponseToDownload = '';
              this.isResponseTooLarge = true;
              this._snackBar.open('Response received but too large to process. Please reduce data size.', 'Close', {
                duration: 4000,
                panelClass: ['warning-snackbar']
              });
            }
            
            // Check if response was too large to display
            if (this.isResponseTooLarge) {
              this._snackBar.open('API call successful. Response is large - use download button.', 'Close', {
                duration: 4000,
                panelClass: ['warning-snackbar']
              });
            } else {
              this._snackBar.open('API call successful', 'Close', {
                duration: 4000,
                panelClass: ['success-snackbar']
              });
            }
          } catch (formatError) {
            console.error('Error processing response:', formatError);
            this.dataServiceResponse = 'Error processing response. Response may be too large.';
            this.dataServiceResponseToDownload = '';
            this.isResponseTooLarge = true;
            this._snackBar.open('Response received but failed to process', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
          }
        }
        
        // Force change detection to update UI
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      },
      (error: any) => {
        // Always stop loading on error
        this.crudLoading = false;
        this.dataServiceResponse = '';
        this.dataServiceResponseToDownload = '';
        this.isResponseTooLarge = false;
        
        console.error('Error calling data service:', error);
        this._snackBar.open('Error occurred while calling API', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        
        // Force change detection to update UI
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }
    );
  }

  /**
   * Format JSON response with syntax highlighting (similar to objToJsonView)
   * Returns object with formatted response and metadata about size
   */
  private formatJsonResponse(obj: any): { formattedResponse: string; isTooLarge: boolean } {
    try {
      // Handle null, undefined, or empty values
      if (obj === null) {
        return { formattedResponse: 'null', isTooLarge: false };
      }
      if (obj === undefined) {
        return { formattedResponse: '', isTooLarge: false };
      }
      
      // First, try to stringify without formatting to check size
      const unformatted = JSON.stringify(obj);
      const sizeInBytes = unformatted.length;
      
      // Check if response is too large (> 5MB)
      const MAX_SIZE = 5 * 1024 * 1024; // 5MB limit
      if (sizeInBytes > MAX_SIZE) {
        console.warn('Response is too large to display:', sizeInBytes, 'characters');
        
        // Return truncated response without the warning message
        const truncated = unformatted.substring(0, 5000);
        return { 
          formattedResponse: truncated + '\n\n... (truncated)',
          isTooLarge: true
        };
      }
      
      // Format as JSON with indentation
      const formatted = JSON.stringify(obj, null, 2);
      
      // Return empty string if result is undefined or just whitespace
      const finalResponse = formatted && formatted.trim() ? formatted : '';
      return { formattedResponse: finalResponse, isTooLarge: false };
    } catch (e) {
      console.error('Error formatting JSON:', e);
      
      // Handle RangeError specifically (string too large)
      if (e instanceof RangeError) {
        return { 
          formattedResponse: 'Response is too large to process.',
          isTooLarge: true
        };
      }
      
      // Return string representation as fallback
      const fallback = obj ? String(obj) : '';
      return { formattedResponse: fallback, isTooLarge: false };
    }
  }

  /**
   * Download response as JSON file
   */
  downloadResponseAsFile() {
    if (!this.dataServiceResponseToDownload) {
      this._snackBar.open('No response data to download. Response may be too large or empty.', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }
    
    try {
      const link = document.createElement("a");
      const fileName = `${this.rowData.SERVICE_KEY}_${this.rowData.SERVICE_NAME}_response.json`;
      link.download = fileName;
      
      // For large files, use Blob instead of data URL to avoid URI length limits
      const blob = new Blob([this.dataServiceResponseToDownload], { type: 'application/json' });
      link.href = URL.createObjectURL(blob);
      link.click();
      
      // Clean up the object URL after download
      setTimeout(() => URL.revokeObjectURL(link.href), 100);
      
      this._snackBar.open('Response downloaded successfully', 'Close', {
        duration: 4000,
        panelClass: ['success-snackbar']
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      this._snackBar.open('Error occurred while downloading response', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
    }
  }

  /**
   * Copy response to clipboard
   */
  copyClipboard() {
    // Check if there's data to copy
    if (!this.dataServiceResponse) {
      this._snackBar.open('No response data to copy', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }
    
    // Check if response is too large (truncated)
    if (this.isResponseTooLarge) {
      this._snackBar.open('Response is too large to copy (truncated). Please use the download button for complete data.', 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }
    
    const copyTextarea = this.dataServiceResponse;
    const textArea = document.createElement('textarea');
    textArea.textContent = copyTextarea;
    document.body.append(textArea);
    textArea.select();
    textArea.setSelectionRange(0, 0);
    
    try {
      navigator.clipboard.writeText(textArea.value).then(() => {
        this.copyMessage = 'Response successfully copied to your clipboard';
        
        // Clear copy message after 3 seconds
        setTimeout(() => {
          this.copyMessage = '';
          this.cdr.detectChanges();
        }, 3000);
        
        textArea.remove();
        this.cdr.detectChanges();
      }).catch((err) => {
        console.error('Error copying to clipboard:', err);
        this.copyMessage = 'Failed to copy';
        this._snackBar.open('Failed to copy to clipboard', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        textArea.remove();
        this.cdr.detectChanges();
      });
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      this.copyMessage = 'Failed to copy';
      this._snackBar.open('Failed to copy to clipboard', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      textArea.remove();
      this.cdr.detectChanges();
    }
  }

  /**
   * Cancel and close modal
   */
  cancel() {
    // CRITICAL: Use EventEmitter for AngularJS modal dismissal
    this.requestGridUpdate.emit('cancel');
  }
}

